import React, { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import { FiArrowRight, FiMessageSquare, FiPhoneCall } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import PopularPackageLinks from "./PopularPackageLinks";
import { addEnquiriesData, addScheduleCallData } from "../services/enquirieService";
import PackagesCards from "./PackagesCards";
import { ThemeContext } from "../context/ThemeContext"; // ✅ ThemeContext import

const Enquiry = () => {
  const { theme } = useContext(ThemeContext); // ✅ Use theme

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigate = useNavigate();

  const formVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const staggerChildren = {
    visible: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    travelDate: "",
    destination: "",
    leavingFrom: "",
    totalPerson: "",
    bookingWithin: "",
    totalDays: "7",
  });

  const [errors, setErrors] = useState({});
  const [callbackPhone, setCallbackPhone] = useState("");
  const [callbackError, setCallbackError] = useState("");

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => ({ ...prev, [id]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.phone) newErrors.phone = "Phone is required";
    if (!formData.travelDate) newErrors.travelDate = "Travel date is required";
    if (!formData.destination) newErrors.destination = "Destination is required";
    if (!formData.leavingFrom) newErrors.leavingFrom = "Departure city is required";
    if (!formData.totalPerson) newErrors.totalPerson = "Number of adults is required";
    if (!formData.bookingWithin) newErrors.bookingWithin = "Booking timeline is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        travelDate: formData.travelDate,
        destination: formData.destination,
        leavingFrom: formData.leavingFrom,
        totalPerson: parseInt(formData.totalPerson.match(/\d+/)[0]),
        totalDays: 7,
        bookingWithin:
          formData.bookingWithin === "1-2 days" ? 2 :
            formData.bookingWithin === "1 Week" ? 7 :
              formData.bookingWithin === "2 Weeks" ? 14 : 30
      };

      await addEnquiriesData(payload);
      navigate("/thank-you");
    } catch (error) {
      console.error("Submission failed:", error);
      toast.error("Failed to submit enquiry. Please try again.");
    }
  };

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    if (!callbackPhone) {
      setCallbackError("Phone number is required");
      return;
    }

    try {
      await addScheduleCallData({ phone: callbackPhone, status: "Pending" });
      toast.success("Callback scheduled successfully!");
      setCallbackPhone("");
      setCallbackError("");
    } catch (error) {
      console.error("Callback request failed:", error);
      toast.error("Failed to schedule callback. Please try again.");
    }
  };

  return (
    <section
      className={`${theme === "dark" ? "dark" : ""
        } relative bg-gradient-to-b from-slate-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 py-16 text-gray-900 dark:text-gray-100`}
    >
      {/* Hero Section */}
      <motion.div className="relative h-96 bg-blue-900 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-800 to-teal-600 opacity-90" />
        <div className="absolute inset-0 bg-black opacity-30" />
        <motion.div
          className="relative z-10 text-center"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2 sm:mb-4 text-center sm:text-left px-2">
            Start Your <span className="text-[#66ccff]">Journey</span> with{" "}
            <span className="text-[#66ccff]">Tripocation Holiday Pvt. Ltd.</span>
          </h1>

          <p className="text-sm sm:text-base md:text-xl text-blue-100 text-center sm:text-left px-2">
            Get personalized travel solutions from our experts
          </p>
        </motion.div>
      </motion.div>

      {/* Enquiry Form Section */}
      <motion.section
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-4xl mx-auto p-8 -mt-24 relative z-20"
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 100 }}
      >
        <motion.div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-2">Get Your Custom Quote</h2>
          <p className="text-gray-500 dark:text-gray-300">We'll respond within 24 hours</p>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          variants={staggerChildren}
          initial="hidden"
          animate="visible"
        >
          {[
            { id: "name", label: "Your Name", type: "text", placeholder: "Enter your name" },
            { id: "phone", label: "Phone Number", type: "tel", placeholder: "+1 234 567 890" },
            { id: "travelDate", label: "Travel Date", type: "date" },
          ].map(({ id, label, type, placeholder }) => (
            <motion.div key={id} variants={formVariants} className="space-y-2">
              <label htmlFor={id} className="block text-sm font-medium">
                {label}
              </label>
              <input
                id={id}
                type={type}
                value={formData[id]}
                onChange={handleChange}
                placeholder={placeholder}
                className={`w-full px-4 py-3 rounded-lg border ${errors[id] ? "border-red-500" : "border-gray-200 dark:border-gray-600"
                  } bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all`}
              />
              {errors[id] && <p className="text-sm text-red-500">{errors[id]}</p>}
            </motion.div>
          ))}

          {/* Destination */}
          <motion.div variants={formVariants} className="space-y-2">
            <label htmlFor="destination" className="block text-sm font-medium">Destination</label>
            <select
              id="destination"
              value={formData.destination}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg border ${errors.destination ? "border-red-500" : "border-gray-200 dark:border-gray-600"
                } bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100`}
            >
              <option value="">Select Destination</option>
              {[
                "Dubai", "Thailand", "Maldives", "Bali", "Singapore",
                "Sri Lanka", "Malaysia", "Vietnam", "Kerala", "Goa",
                "Himachal", "Kashmir", "Other"
              ].map((dest) => <option key={dest} value={dest}>{dest}</option>)}
            </select>
            {errors.destination && <p className="text-sm text-red-500">{errors.destination}</p>}
          </motion.div>

          {/* Departure City */}
          <motion.div variants={formVariants} className="space-y-2">
            <label htmlFor="leavingFrom" className="block text-sm font-medium">Departure City</label>
            <select
              id="leavingFrom"
              value={formData.leavingFrom}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg border ${errors.leavingFrom ? "border-red-500" : "border-gray-200 dark:border-gray-600"
                } bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100`}
            >
              <option value="">Select Departure City</option>
              {[
                "Ahmedabad", "Delhi", "Mumbai", "Bangalore", "Hyderabad",
                "Pune", "Chennai", "Kolkata", "Other"
              ].map((city) => <option key={city} value={city}>{city}</option>)}
            </select>
            {errors.leavingFrom && <p className="text-sm text-red-500">{errors.leavingFrom}</p>}
          </motion.div>

          {/* Adults */}
          <motion.div variants={formVariants} className="space-y-2">
            <label htmlFor="totalPerson" className="block text-sm font-medium">Adults</label>
            <select
              id="totalPerson"
              value={formData.totalPerson}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg border ${errors.totalPerson ? "border-red-500" : "border-gray-200 dark:border-gray-600"
                } bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100`}
            >
              <option value="">Select Adults</option>
              {[...Array(10).keys()].map((i) => {
                const num = i + 1;
                return (
                  <option key={num} value={num}>
                    {num} {num === 1 ? "Adult" : "Adults"}
                  </option>
                );
              })}
            </select>
            {errors.totalPerson && <p className="text-sm text-red-500">{errors.totalPerson}</p>}
          </motion.div>

          {/* Booking Timeline */}
          <motion.div variants={formVariants} className="space-y-2">
            <label htmlFor="bookingWithin" className="block text-sm font-medium">Booking Timeline</label>
            <select
              id="bookingWithin"
              value={formData.bookingWithin}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg border ${errors.bookingWithin ? "border-red-500" : "border-gray-200 dark:border-gray-600"
                } bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100`}
            >
              <option value="">Select Booking Timeline</option>
              <option value="1-2 days">1-2 days</option>
              <option value="1 Week">1 Week</option>
              <option value="2 Weeks">2 Weeks</option>
              <option value="1 Month">1 Month</option>
            </select>
            {errors.bookingWithin && <p className="text-sm text-red-500">{errors.bookingWithin}</p>}
          </motion.div>

          {/* Submit */}
          <motion.div className="md:col-span-2 flex justify-center" variants={formVariants}>
            <motion.button
              type="submit"
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-[#007bff] text-white px-8 py-4 rounded-xl transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Get Free Quote <FiArrowRight className="text-lg" />
            </motion.button>
          </motion.div>
        </motion.form>
      </motion.section>

      {/* Contact Section */}
      <motion.div
        className="max-w-4xl mx-auto mt-12 p-8 bg-gradient-to-r from-blue-800 to-teal-700 rounded-2xl shadow-xl text-white"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <h3 className="text-2xl font-bold">Need Immediate Assistance?</h3>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/10 rounded-lg">
                <FiPhoneCall className="text-2xl text-teal-300" />
              </div>
              <div>
                <p className="font-sm font-semibold">24/7 Support Line</p>
                <p className="text-sm">+91 92201 98202</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/10 rounded-lg">
                <FiMessageSquare className="text-2xl text-teal-300" />
              </div>
              <div>
                <p className="font-sm font-semibold">Email Support</p>
                <p className="text-sm">prince@tripocationholiday.com</p>
                <p className="text-sm">sachin@tripocationholiday.com</p>
              </div>
            </div>
          </div>

          <motion.form onSubmit={handleCallbackSubmit} className="space-y-4">
            <h3 className="text-2xl font-bold">Request Callback</h3>
            <input
              type="tel"
              value={callbackPhone}
              onChange={(e) => {
                setCallbackPhone(e.target.value);
                setCallbackError("");
              }}
              placeholder="Enter your phone number"
              className={`w-full px-4 py-3 rounded-lg bg-white/10 border ${callbackError ? "border-red-500" : "border-white/20"
                } placeholder:text-white/70 focus:ring-2 focus:ring-teal-300 focus:border-transparent`}
            />
            {callbackError && <p className="text-red-400 text-sm">{callbackError}</p>}
            <motion.button
              type="submit"
              className="w-full flex items-center justify-center gap-2 bg-[#007bff] text-white px-6 py-3 rounded-lg font-semibold transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Schedule Call
            </motion.button>
          </motion.form>
        </div>
      </motion.div>

      <PackagesCards />
      <PopularPackageLinks />
    </section>
  );
};

export default Enquiry;